import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, User, ArrowRight, Search, Tag, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import Header from "../../components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "all", label: "Toate", color: "bg-stone-100 text-stone-700" },
  { id: "sfaturi", label: "Sfaturi cumpărători", color: "bg-blue-100 text-blue-700" },
  { id: "credite", label: "Ghid credite", color: "bg-emerald-100 text-emerald-700" },
  { id: "piata", label: "Piața imobiliară", color: "bg-amber-100 text-amber-700" },
  { id: "legislatie", label: "Legislație", color: "bg-purple-100 text-purple-700" },
];

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      const response = await axios.get(`${API}/blog/articles?${params}`);
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find(c => c.id === category);
    return cat?.color || "bg-stone-100 text-stone-700";
  };

  const getCategoryLabel = (category) => {
    const cat = CATEGORIES.find(c => c.id === category);
    return cat?.label || category;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-stone-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Blog Imofit</h1>
          <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
            Sfaturi, ghiduri și noutăți din piața imobiliară românească
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              placeholder="Caută articole..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60"
            />
          </div>
        </div>
      </div>
      
      {/* Categories */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 justify-center">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat.id
                  ? "bg-emerald-600 text-white shadow-md"
                  : "bg-white text-stone-600 hover:bg-stone-100 border border-stone-200"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Articles Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-stone-500 text-lg">Nu am găsit articole.</p>
            <p className="text-stone-400 text-sm mt-2">Revino în curând pentru conținut nou!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map(article => (
              <Link 
                key={article.id} 
                to={`/blog/${article.slug}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-stone-100 relative overflow-hidden">
                  {article.thumbnail ? (
                    <img 
                      src={article.thumbnail} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-100 to-emerald-200">
                      <span className="text-4xl">📝</span>
                    </div>
                  )}
                  <Badge className={`absolute top-3 left-3 ${getCategoryColor(article.category)}`}>
                    {getCategoryLabel(article.category)}
                  </Badge>
                </div>
                
                {/* Content */}
                <div className="p-5">
                  <h2 className="font-bold text-lg text-stone-800 mb-2 line-clamp-2 group-hover:text-emerald-700 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-stone-500 text-sm line-clamp-3 mb-4">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-stone-400">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {article.author_name || "Admin"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(article.published_at).toLocaleDateString('ro-RO')}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
